import React, { useState, useEffect } from "react";

/**
 * DataSourceDrawer - CRUD Management Panel
 * Allows creating, editing, and deleting data sources.
 */
export default function DataSourceDrawer({ 
  dataSources, 
  onClose, 
  onCopyKey, 
  onRefreshDataSources, 
  axiosPrivate, 
  groupCode, 
  projectCode 
}) {
  // State: View Mode ('list', 'create', 'edit')
  const [viewMode, setViewMode] = useState('list');
  const [editingId, setEditingId] = useState(null);
  
  // State: Form
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // State: Delete Confirmation
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Constants
  const INITIAL_FORM = {
    source_key: "",
    source_type: "column",
    source_name: "",
    description: "",
    target_database: "master",
    target_table: "",
    target_column: "",
    query_template: "",
    compute_function: "",
    compute_params: "",
    default_value: "",
    format: ""
  };

  const COMPUTE_FUNCTIONS = [
    { value: "countRiskByLevel", label: "위험성 평가 등급별 카운트" },
    { value: "countAccidents", label: "사고 발생 건수" },
    { value: "calcMaintenanceCompliance", label: "예방 점검 이행율" },
    { value: "calcDaysUntilMaintenance", label: "다음 점검일까지 남은 일수" },
    { value: "getLatestInspectionStatus", label: "최신 점검 결과" },
    { value: "countRiskMeasureStatus", label: "위험성 개선 조치 현황" },
    { value: "countTotalRisk", label: "총 위험성 평가 건수" }
  ];

  // Handlers
  const handleCreate = () => {
    setFormData({ ...INITIAL_FORM });
    setError(null);
    setViewMode('create');
  };

  const handleEdit = (source) => {
    setFormData({
      ...INITIAL_FORM,
      ...source,
      // Ensure nulls are empty strings for inputs
      description: source.description || "",
      target_table: source.target_table || "",
      target_column: source.target_column || "",
      query_template: source.query_template || "",
      compute_function: source.compute_function || "",
      compute_params: source.compute_params || "",
      default_value: source.default_value || "",
      format: source.format || ""
    });
    setEditingId(source.source_id);
    setError(null);
    setViewMode('edit');
  };

  const handleCancel = () => {
    setViewMode('list');
    setEditingId(null);
    setError(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!formData.source_key) {
      setError("키(Key)는 필수입니다.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        group_code: groupCode,
        project_code: projectCode
      };

      if (viewMode === 'create') {
        await axiosPrivate.post(`/dt/mchinfomod/admin/datasource`, payload);
      } else {
        await axiosPrivate.put(`/dt/mchinfomod/admin/datasource/${editingId}`, payload);
      }

      await onRefreshDataSources();
      setViewMode('list');
    } catch (err) {
      console.error("Save error:", err);
      setError(err.response?.data?.message || "저장 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      await axiosPrivate.delete(`/dt/mchinfomod/admin/datasource/${id}?group_code=${groupCode}&project_code=${projectCode}`);
      setDeleteConfirmId(null);
      await onRefreshDataSources();
    } catch (err) {
      console.error("Delete error:", err);
      if (err.response?.status === 409) {
        // Show usage warning
        setError(err.response.data.message || "사용 중인 데이터 소스는 삭제할 수 없습니다.");
      } else {
        setError("삭제 중 오류가 발생했습니다.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Render Helpers
  const renderBadge = (type) => {
    const colors = {
      column: "bg-blue-500/20 text-blue-300 border-blue-500/30",
      query: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
      computed: "bg-purple-500/20 text-purple-300 border-purple-500/30",
      api: "bg-amber-500/20 text-amber-300 border-amber-500/30"
    };
    return (
      <span className={`px-1.5 py-0.5 text-[10px] rounded border ${colors[type] || colors.column} uppercase tracking-wide font-medium`}>
        {type}
      </span>
    );
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-[2px] z-40 transition-opacity"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-[480px] bg-slate-900 border-l border-slate-800 shadow-2xl z-50 flex flex-col font-sans text-slate-300">
        
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-900/50">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-black text-slate-100 uppercase tracking-wider">
              {viewMode === 'list' ? '데이터 소스 관리' : (viewMode === 'create' ? '새 데이터 소스' : '데이터 소스 수정')}
            </h2>
            {viewMode === 'list' && (
              <span className="bg-slate-800 text-slate-400 text-[10px] px-2 py-0.5 rounded-full font-mono">
                {dataSources.length}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {viewMode === 'list' && (
              <button
                onClick={handleCreate}
                className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs px-3 py-1.5 rounded transition-all font-medium shadow-lg shadow-indigo-900/20"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
                추가
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded hover:bg-slate-800 text-slate-500 hover:text-slate-200 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
          
          {/* Error Banner */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
              <svg className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
              <div className="flex-1 text-xs text-red-200 leading-relaxed whitespace-pre-wrap">{error}</div>
              <button onClick={() => setError(null)} className="text-red-400 hover:text-red-200"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
            </div>
          )}

          {viewMode === 'list' ? (
            /* --- LIST VIEW --- */
            <div className="space-y-3">
              {dataSources.length === 0 ? (
                <div className="text-center text-slate-500 text-sm py-12 flex flex-col items-center gap-3">
                  <svg className="w-10 h-10 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" /></svg>
                  <p>등록된 데이터 소스가 없습니다.</p>
                </div>
              ) : (
                dataSources.map((source) => (
                  <div key={source.source_id || source.source_key} className="bg-slate-800/40 hover:bg-slate-800 border border-slate-700/50 rounded-lg p-4 group transition-all relative overflow-hidden">
                    
                    {/* Delete Confirmation Overlay */}
                    {deleteConfirmId === source.source_id && (
                      <div className="absolute inset-0 bg-slate-900/95 z-10 flex flex-col items-center justify-center text-center p-4 animate-fadeIn">
                        <p className="text-sm text-slate-300 mb-3">정말 삭제하시겠습니까?</p>
                        <div className="flex gap-2">
                          <button 
                            onClick={() => handleDelete(source.source_id)}
                            className="bg-red-600 hover:bg-red-500 text-white text-xs px-3 py-1.5 rounded"
                            disabled={loading}
                          >
                            {loading ? '삭제 중...' : '삭제 확인'}
                          </button>
                          <button 
                            onClick={() => { setDeleteConfirmId(null); setError(null); }}
                            className="bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs px-3 py-1.5 rounded"
                          >
                            취소
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div 
                        className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => onCopyKey(source.source_key)}
                        title="키 복사"
                      >
                        <code className="text-sm font-mono font-bold text-indigo-400 bg-indigo-900/10 px-1.5 py-0.5 rounded border border-indigo-500/10">
                          {source.source_key}
                        </code>
                        <svg className="w-3.5 h-3.5 text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button 
                          onClick={() => handleEdit(source)}
                          className="p-1.5 text-slate-400 hover:text-indigo-400 hover:bg-indigo-500/10 rounded transition-colors"
                          title="수정"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                        </button>
                        <button 
                          onClick={() => setDeleteConfirmId(source.source_id)}
                          className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                          title="삭제"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 text-xs">
                        {renderBadge(source.source_type)}
                        <span className="text-slate-500">|</span>
                        <span className="text-slate-400">DB: <span className="text-slate-300">{source.target_database}</span></span>
                      </div>
                      {source.source_name && (
                        <div className="text-sm text-slate-200 font-medium">{source.source_name}</div>
                      )}
                      {source.description && (
                        <div className="text-xs text-slate-500 line-clamp-1">{source.description}</div>
                      )}
                      {source.default_value && (
                         <div className="text-[10px] text-slate-500 mt-1 pt-1 border-t border-slate-700/50 flex gap-2">
                           <span>기본값: {source.default_value}</span>
                           {source.format && <span>형식: {source.format}</span>}
                         </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            /* --- FORM VIEW (CREATE / EDIT) --- */
            <div className="space-y-5 animate-slideIn">
              
              {/* Basic Info */}
              <section className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">Key (필수)</label>
                    <input
                      name="source_key"
                      value={formData.source_key}
                      onChange={handleChange}
                      disabled={viewMode === 'edit'}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:border-indigo-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed font-mono"
                      placeholder="KEY_NAME"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">유형</label>
                    <select
                      name="source_type"
                      value={formData.source_type}
                      onChange={handleChange}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:border-indigo-500 focus:outline-none"
                    >
                      <option value="column">DB Column</option>
                      <option value="query">Custom Query</option>
                      <option value="computed">Computed</option>
                      <option value="api">External API</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">이름</label>
                  <input
                    name="source_name"
                    value={formData.source_name}
                    onChange={handleChange}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:border-indigo-500 focus:outline-none"
                    placeholder="표시할 이름"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">설명</label>
                  <input
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:border-indigo-500 focus:outline-none"
                    placeholder="데이터 소스에 대한 설명"
                  />
                </div>
              </section>

              <div className="h-px bg-slate-800 my-2"></div>

              {/* Type Specific Fields */}
              <section className="space-y-4">
                <div className="flex items-center gap-2 mb-2">
                   <span className="text-xs font-bold text-indigo-400 uppercase">상세 설정</span>
                   <div className="h-px bg-slate-800 flex-1"></div>
                </div>

                {formData.source_type === 'column' && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">대상 DB</label>
                        <select
                          name="target_database"
                          value={formData.target_database}
                          onChange={handleChange}
                          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:border-indigo-500 focus:outline-none"
                        >
                          <option value="master">Master DB</option>
                          <option value="info">Info DB</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">테이블</label>
                        <input
                          name="target_table"
                          value={formData.target_table}
                          onChange={handleChange}
                          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:border-indigo-500 focus:outline-none font-mono"
                          placeholder="table_name"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">컬럼명</label>
                      <input
                        name="target_column"
                        value={formData.target_column}
                        onChange={handleChange}
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:border-indigo-500 focus:outline-none font-mono"
                        placeholder="column_name"
                      />
                    </div>
                  </>
                )}

                {formData.source_type === 'query' && (
                  <>
                     <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">대상 DB</label>
                        <select
                          name="target_database"
                          value={formData.target_database}
                          onChange={handleChange}
                          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:border-indigo-500 focus:outline-none"
                        >
                          <option value="master">Master DB</option>
                          <option value="info">Info DB</option>
                        </select>
                      </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">SQL 쿼리</label>
                      <textarea
                        name="query_template"
                        value={formData.query_template}
                        onChange={handleChange}
                        rows={6}
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-200 font-mono focus:border-indigo-500 focus:outline-none leading-relaxed"
                        placeholder="SELECT count(*) FROM table WHERE..."
                      />
                      <p className="text-[10px] text-slate-500 mt-1">
                        * 파라미터는 ? 또는 :param 형식으로 사용할 수 없습니다. 현재는 정적 쿼리만 지원합니다.
                      </p>
                    </div>
                  </>
                )}

                {formData.source_type === 'computed' && (
                  <>
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">계산 함수</label>
                      <select
                        name="compute_function"
                        value={formData.compute_function}
                        onChange={handleChange}
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:border-indigo-500 focus:outline-none"
                      >
                        <option value="">함수 선택...</option>
                        {COMPUTE_FUNCTIONS.map(fn => (
                          <option key={fn.value} value={fn.value}>{fn.label} ({fn.value})</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">파라미터 (JSON)</label>
                      <textarea
                        name="compute_params"
                        value={formData.compute_params}
                        onChange={handleChange}
                        rows={3}
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-200 font-mono focus:border-indigo-500 focus:outline-none"
                        placeholder='{ "threshold": 50 }'
                      />
                    </div>
                  </>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">기본값</label>
                    <input
                      name="default_value"
                      value={formData.default_value}
                      onChange={handleChange}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:border-indigo-500 focus:outline-none"
                      placeholder="0, -"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">표시 형식</label>
                    <input
                      name="format"
                      value={formData.format}
                      onChange={handleChange}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:border-indigo-500 focus:outline-none"
                      placeholder="건, %, 원"
                    />
                  </div>
                </div>
              </section>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-slate-800">
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-800 text-white py-2.5 rounded-lg text-sm font-bold transition-all shadow-lg shadow-indigo-900/30"
                >
                  {loading ? '저장 중...' : '저장'}
                </button>
                <button
                  onClick={handleCancel}
                  className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 py-2.5 rounded-lg text-sm font-bold transition-all border border-slate-700"
                >
                  취소
                </button>
              </div>

            </div>
          )}
        </div>
      </div>
    </>
  );
}
